import Vue from 'vue'
import Router from 'vue-router'
import store from '../store/'
import Home from '../views/Home.vue'
import SignUp from '../views/SignUp.vue'
import Dashboard from '../views/Dashboard.vue'
import Admin from '../views/Admin.vue'
import MessageCenter from '../views/MessageCenter.vue'
import Support from '../views/Support.vue'
import ApparatusDetails from '../views/ApparatusDetails.vue'
import DeviceDetails from '@/components/ApparatusDetails/DeviceDetails.vue'
import StatusHistory from '@/components/ApparatusDetails/StatusHistory.vue'
import ExerciseSchedule from '@/components/ApparatusDetails/ExerciseSchedule.vue'
import Maintenance from '@/components/ApparatusDetails/Maintenance.vue'
import Warranty from '@/components/ApparatusDetails/Warranty.vue'
import TechnicalSpecs from '@/components/ApparatusDetails/TechnicalSpecs.vue'
import PreferredDealer from '@/components/ApparatusDetails/PreferredDealer.vue'
import Subscription from '@/components/ApparatusDetails/Subscription.vue'
import TankDetails from '@/components/ApparatusDetails/TankDetails.vue'
import FuelConsumption from '@/components/ApparatusDetails/FuelConsumption.vue'
import FuelLevelWarning from '@/components/ApparatusDetails/FuelLevelWarning.vue'
import AccountInformation from '@/components/AccountSettings/AccountInformation.vue'
import SettingsSubscriptions from '@/components/AccountSettings/SettingsSubscriptions/SettingsSubscriptions.vue'
import PaymentInformation from '@/components/AccountSettings/PaymentInformation.vue'
import DocumentsStatements from '@/components/AccountSettings/DocumentsStatements.vue'
import DisplayPreferences from '@/components/AccountSettings/DisplayPreferences.vue'
import SettingsNotifications from '@/components/AccountSettings/SettingsNotifications.vue'
import AccountSettings from '../views/AccountSettings.vue'
import CreateAccount from '../views/CreateAccount.vue'
import GetStarted from '../views/GetStarted.vue'
import QrScan from '../views/QrScan.vue'
import LandingDownload from '../views/LandingDownload.vue'
import { vueAppApiBaseAddress, vueAppBaseAddress } from '@/utils/mixins'
import i18n from '@/lang/'

Vue.use(Router)
const B2C_CHALLENGE_URL = '../api/Auth/SignIn'
const AUTH0_CHALLENGE_URL = '../api/Auth/Auth0/SignIn'
const DESTINATION_QUERY_PARAM = 'destination'
const AUTH_PROVIDER_QUERY_PARAM = 'authProvider'
const B2C_EMAIL_QUERY_PARAM = 'email'
const AUTH0_EMAIL_QUERY_PARAM = 'userEmail'
const SINGLESIGNONEMAIL_QUERY_PARAM = 'singleSignOnEmail'

const AUTH0_PROVIDER = 'auth0'

const isIos = navigator.userAgent.toLowerCase().match(/(ios)|(ipad)|(iphone)/)
const isAndroid = navigator.userAgent.toLowerCase().includes('android')

const apparatusIdIsValid = apparatusId => {
  return isNaN(apparatusId) === false && apparatusId
}

let router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      beforeEnter: (to, from, next) => {
        if (document.cookie.indexOf('MobileLinkClientCookie=') != -1) {
          next({ path: '/dashboard' })
        } else if (to.query.singleSignOnEmail) {
          let params = new URLSearchParams()

          let destination = new URL(to.fullPath, vueAppBaseAddress())
          let destinationParams = new URLSearchParams(destination.search)

          // Extract existing destination parameter if present, otherwise use current path
          let destinationPath = destinationParams.get(DESTINATION_QUERY_PARAM)

          if (destinationPath) {
            // Use existing destination parameter
            destinationPath = decodeURIComponent(destinationPath)
          } else {
            // Construct destination from current path, removing auth-related params
            destinationParams.delete(SINGLESIGNONEMAIL_QUERY_PARAM)
            destinationParams.delete(AUTH_PROVIDER_QUERY_PARAM)

            destinationPath = destination.pathname
            if (destinationParams.toString()) {
              destinationPath = `${destination.pathname}?${destinationParams}`
            }
          }

          params.append(DESTINATION_QUERY_PARAM, destinationPath)
          let challengeURL = B2C_CHALLENGE_URL

          if (to?.query?.authProvider && to.query.authProvider === AUTH0_PROVIDER) {
            challengeURL = AUTH0_CHALLENGE_URL
            params.append(AUTH0_EMAIL_QUERY_PARAM, to.query.singleSignOnEmail)
          } else {
            params.append(B2C_EMAIL_QUERY_PARAM, to.query.singleSignOnEmail)
          }

          const authPath = new URL(challengeURL, vueAppApiBaseAddress())

          window.location.assign(`${authPath}?${params}`)
        } else {
          next()
        }
      },
      meta: {
        protected: false
      },
      alias: ['/correlationError', '/expiredLink', '/loginError', '/inviteError']
    },
    {
      path: '/dashboard/:apparatusId/:actionType',
      name: 'incompleteApparatus',
      component: Dashboard,
      meta: {
        protected: true
      }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: Dashboard,
      meta: {
        protected: true
      }
    },
    {
      path: '/dashboard/:apparatusType/:apparatusId',
      name: 'beginEnrollmentFlow',
      component: Dashboard,
      meta: {
        protected: true
      }
    },
    {
      path: '/details/:apparatusId',
      component: ApparatusDetails,
      redirect: to => {
        return { name: 'deviceDetails', params: { apparatusId: to.params.apparatusId } }
      },
      beforeEnter: async (to, from, next) => {
        if (!store.state.application.currentAppConfig) store.dispatch('getApplicationConfiguration', 'Any')

        const accountApparatuses = store.state?.apparatus?.apparatuses
        if (accountApparatuses) {
          //Check if account apparatuses have been initialized
          if (
            //if so, check if desired apparatusId is included in account apparatuses
            accountApparatuses.some(apparatus => apparatus.apparatusId.toString() === to.params.apparatusId)
          ) {
            return next()
          }
        }
        //If account apparatuses have not been initialized, get apparatus list
        const response = await store.dispatch('getApparatusesWithResponse')
        if (
          response?.data && //search apparatus list for desired apparatusId
          response?.data.some(apparatus => apparatus.apparatusId.toString() === to.params.apparatusId)
        ) {
          return next()
        }
        //If desired apparatusId is not found, display toast and return to dashboard
        store.dispatch('displayToast', {
          title: i18n.t('xAnErrorHasOccurred'),
          message: i18n.t('xAccessDenied'),
          variant: 'danger'
        })
        return next({ name: 'dashboard' })
      },
      children: [
        {
          path: 'deviceDetails',
          name: 'deviceDetails',
          component: DeviceDetails
        },
        {
          path: 'statusHistory',
          name: 'statusHistory',
          component: StatusHistory,
          meta: {
            label: 'Status History'
          }
        },
        {
          path: 'exerciseSchedule',
          name: 'exerciseSchedule',
          component: ExerciseSchedule,
          meta: {
            label: 'Exercise Schedule'
          }
        },
        {
          path: 'maintenance',
          name: 'maintenance',
          component: Maintenance,
          meta: {
            label: 'Maintenance'
          }
        },
        {
          path: 'warranty',
          name: 'warranty',
          component: Warranty,
          meta: {
            label: 'Warranty'
          }
        },
        {
          path: 'technicalSpecs',
          name: 'technicalSpecs',
          component: TechnicalSpecs,
          meta: {
            label: 'Technical Specs'
          }
        },
        {
          path: 'preferredDealer',
          name: 'preferredDealer',
          component: PreferredDealer,
          meta: {
            label: 'Preferred Dealer'
          }
        },
        {
          path: 'subscription',
          name: 'subscription',
          component: Subscription,
          meta: {
            label: 'Subscription'
          }
        },
        {
          path: 'tankDetails',
          name: 'tankDetails',
          component: TankDetails,
          meta: {
            label: 'Tank Details'
          }
        },
        {
          path: 'fuelConsumption',
          name: 'fuelConsumption',
          component: FuelConsumption,
          meta: {
            label: 'Fuel Consumption'
          }
        },
        {
          path: 'fuelLevelWarning',
          name: 'fuelLevelWarning',
          component: FuelLevelWarning,
          meta: {
            label: 'Fuel Level Warning'
          }
        },
        {
          path: 'fuelProvider',
          name: 'fuelProvider',
          component: PreferredDealer,
          meta: {
            label: 'Fuel Provider'
          }
        },
        {
          path: 'preferredServiceDealer',
          name: 'preferredServiceDealer',
          component: PreferredDealer,
          meta: {
            label: 'Preferred Service Dealer'
          }
        }
      ],
      meta: {
        protected: true
      }
    },
    {
      path: '/messageCenter',
      name: 'messageCenter',
      component: MessageCenter,
      meta: {
        protected: true
      }
    },
    {
      path: '/support',
      name: 'support',
      component: Support,
      meta: {
        protected: true
      }
    },
    {
      path: '/accountSettings',
      name: 'accountSettings',
      component: AccountSettings,
      children: [
        {
          path: 'accountInformation',
          name: 'accountInformation',
          component: AccountInformation,
          meta: {
            label: 'Account Information'
          }
        },
        {
          path: 'paymentInformation',
          name: 'paymentInformation',
          component: PaymentInformation,
          meta: {
            label: 'PaymentInformation'
          },
          props: true
        },
        {
          path: 'settingsSubscriptions',
          name: 'settingsSubscriptions',
          component: SettingsSubscriptions,
          meta: {
            label: 'Subscription'
          },
          props: true
        },
        {
          path: 'documentsStatements',
          name: 'documentsStatements',
          component: DocumentsStatements,
          meta: {
            label: 'Documents and Statements'
          }
        },
        {
          path: 'settingsNotifications',
          name: 'settingsNotifications',
          component: SettingsNotifications,
          meta: {
            label: 'Notifications'
          }
        },
        {
          path: 'displayPreferences',
          name: 'displayPreferences',
          component: DisplayPreferences,
          meta: {
            label: 'Display Preferences'
          }
        }
      ],
      meta: {
        protected: true
      }
    },
    {
      path: '/createAccount',
      name: 'createAccount',
      component: CreateAccount,
      meta: {
        protected: true
      }
    },
    {
      path: '/getstarted',
      name: 'getstarted',
      component: GetStarted,
      meta: {
        protected: false
      }
    },
    {
      path: '/qrscan/:serialNumber/:modelNumber/:btId',
      name: 'qrScan',
      component: QrScan,
      meta: {
        protected: false
      }
    },
    {
      path: '/admin',
      name: 'admin',
      component: Admin,
      meta: {
        protected: true
      }
    },
    {
      path: '/download',
      name: 'landingDownload',
      component: LandingDownload,
      meta: {
        protected: false
      }
    },
    {
      path: '/signup',
      name: 'signup',
      component: SignUp,
      meta: {
        protected: false
      }
    },
    // Deep Links - starting
    // The following path handles all incoming deep links if the user does not have the mobile app installed.
    // We first check the users current platform and redirect them to the correct app store if they are a mobile
    // user. If they are a web user, then the following switch statement will be used to breakdown the link.
    {
      path: '/link/*',
      redirect: to => {
        if (!isIos && !isAndroid) {
          const splitURL = to.path.split('/')
          const deepLinkActionPosition = 2
          const deepLinkMinActionLength = 3
          switch (splitURL[deepLinkActionPosition]) {
            // Link format: <mobilelinkdomain>/"link"/"enroll"/<apparatusType | null>/<apparatusId | null>
            case 'enroll':
              const deepLinkApparatusTypeLocation = 3
              const deepLinkEnrollApparatusIdMinSize = 5
              const deepLinkEnrollApparatusIdLocation = 4
              if (splitURL.length > deepLinkMinActionLength) {
                var apparatusType = parseInt(splitURL[deepLinkApparatusTypeLocation])
                if (apparatusIdIsValid(apparatusType)) {
                  if (
                    splitURL.length >= deepLinkEnrollApparatusIdMinSize &&
                    splitURL[deepLinkEnrollApparatusIdLocation] != ''
                  ) {
                    var apparatusId = parseInt(splitURL[deepLinkEnrollApparatusIdLocation], 10)
                    if (apparatusIdIsValid(apparatusId)) {
                      return {
                        name: 'beginEnrollmentFlow',
                        params: { apparatusType: apparatusType, apparatusId: apparatusId }
                      }
                    }
                  } else {
                    return {
                      name: 'beginEnrollmentFlow',
                      params: { apparatusType: apparatusType, apparatusId: 0 }
                    }
                  }
                }
              }
              break
            // Link format: <mobilelinkdomain>/"link"/"enrollment"/<"complete" | "cancel">/<apparatusId | null>
            case 'enrollment':
              const deepLinkEnrollmentActionLocation = 3
              const deepLinkEnrollmentMinApparatusIdLength = 5
              const deepLinkEnrollmentApparatusIdLocation = 4
              if (splitURL.length > deepLinkMinActionLength) {
                switch (splitURL[deepLinkEnrollmentActionLocation]) {
                  case 'complete':
                    if (splitURL.length >= deepLinkEnrollmentMinApparatusIdLength) {
                      var apparatusId = splitURL[deepLinkEnrollmentApparatusIdLocation]
                      if (apparatusIdIsValid(parseInt(apparatusId, 10))) {
                        return { name: 'deviceDetails', params: { apparatusId: apparatusId } }
                      }
                    }
                    break
                  case 'cancel':
                    return { name: 'home' }
                  default:
                    break
                }
              }
            // Link format: <mobilelinkdomain>/"link"/"qrscan"
            case 'qrscan':
              return {
                name: 'qrScan',
                params: { serialNumber: to.query.sn, modelNumber: to.query.mn, btId: to.query.btid }
              }
            // Link format: <mobilelinkdomain>/"link"/"ecobee"
            case 'ecobee':
              return {
                name: 'getstarted'
              }
            // Link format: <mobilelinkdomain>/"link"/"dashboard"
            case 'dashboard':
              return { name: 'dashboard' }
            // Link format: <mobilelinkdomain>/"link"/"details"/<apparatusId | null>/<"statushistory" | "maintenance" | "preferredDealer" | null>
            case 'details':
              const deepLinkHSBDetailsApparatusIdLocation = 3
              const deepLinkHSBDetailsFeatureMinSize = 5
              const deepLinkHSBDetailsFeatureLocation = 4
              if (splitURL.length > deepLinkMinActionLength) {
                var apparatusId = splitURL[deepLinkHSBDetailsApparatusIdLocation]
                if (apparatusIdIsValid(parseInt(apparatusId, 10))) {
                  if (
                    splitURL.length >= deepLinkHSBDetailsFeatureMinSize &&
                    splitURL[deepLinkHSBDetailsFeatureLocation] != ''
                  ) {
                    switch (splitURL[deepLinkHSBDetailsFeatureLocation]) {
                      case 'statusHistory':
                        return { name: 'statusHistory', params: { apparatusId: apparatusId } }
                      case 'maintenance':
                        return { name: 'maintenance', params: { apparatusId: apparatusId } }
                      case 'preferredDealer':
                        return { name: 'preferredDealer', params: { apparatusId: apparatusId } }
                      case 'deviceDetails':
                        return { name: 'deviceDetails', params: { apparatusId: apparatusId } }
                      default:
                        break
                    }
                  } else {
                    return { name: 'deviceDetails', params: { apparatusId: apparatusId } }
                  }
                }
              }
              break
            // Link format: <mobilelinkdomain>/"link"/"messageCenter"
            case 'messageCenter':
              return { name: 'messageCenter' }
            // Link format: <mobilelinkdomain>/"link"/"accountSettings"/"settingsSubscriptions"
            case 'accountSettings':
              const deepLinkSettingsSubscriptionsLocation = 3
              if (
                splitURL.length > deepLinkMinActionLength &&
                splitURL[deepLinkSettingsSubscriptionsLocation] === 'settingsSubscriptions'
              ) {
                return { name: 'settingsSubscriptions' }
              }
              break
            // Link format: <mobilelinkdomain>/"link"/"notifications"
            case 'notifications':
              return { name: 'settingsNotifications' }
            // Invalid link case
            default:
              break
          }
          // If a check fails or the link is invalid the user is redirected to home
          return { name: 'home' }
        } else if (isIos) {
          window.location.href =
            'https://apps.apple.com/us/app/mobile-link-for-generators/id829129497?itsct=apps_box_badge&amp;itscg=30200'
        } else if (isAndroid) {
          window.location.href = 'https://play.google.com/store/apps/details?id=com.generac.standbystatus'
        }
      }
    },
    // Deep Links - ending
    { path: '*', redirect: '/' }
  ]
})

router.beforeEach((to, from, next) => {
  store.commit('setCurrentRoute', to)
  if (to.matched.some(record => record.meta.protected)) {
    if (to.name == 'admin') {
      store
        .dispatch('authorize', to.name)
        .then(response => {
          next()
        })
        .catch(error => {
          window.location.assign(vueAppApiBaseAddress() + 'Auth/Internal')
        })
    } else if (document.cookie.indexOf('MobileLinkClientCookie=') != -1) {
      next()
    } else if (!store.state.account.user && !store.state.account.newUserEmail) {
      let destination = new URL(to.fullPath, vueAppBaseAddress())
      let destinationParams = new URLSearchParams(destination.search)
      destinationParams.delete(SINGLESIGNONEMAIL_QUERY_PARAM)
      destinationParams.delete(AUTH_PROVIDER_QUERY_PARAM)

      let destinationPath = `${destination.pathname}`
      if (destinationParams.toString()) {
        destinationPath = `${destination.pathname}?${destinationParams}`
      }

      // Redirect to Home with destination parameter instead of directly to auth
      next({
        path: '/',
        query: {
          destination: destinationPath,
          ...(to.query?.singleSignOnEmail && { singleSignOnEmail: to.query.singleSignOnEmail }),
          ...(to.query?.authProvider && { authProvider: to.query.authProvider })
        }
      })
    } else {
      store
        .dispatch('authorize', to.name)
        .then(response => {
          next()
        })
        .catch(error => {
          sessionStorage.setItem('toRoute', JSON.stringify(to))
          next('/')
        })
    }
  } else {
    next()
  }
})

export default router
